import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthLayout } from '@/components/AuthLayout';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Loader2, KeyRound, AlertCircle } from 'lucide-react';

export interface LoginUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  [key: string]: unknown;
}

interface LoginProps {
  onLogin: (token: string, user: LoginUser) => void;
}

function Login({ onLogin }: LoginProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    if (ssoError) {
      setError(ssoError);
    }
  }, [searchParams]);

  const handleSsoLogin = () => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.assign(`${apiUrl}/api/auth/sso/start`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onLogin(data.data.token, data.data.user);
      } else {
        setError(data.error || t('auth.loginError'));
      }
    } catch (err) {
      setError(t('errors.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
            <Link to="/" className="text-link text-sm">
              <ArrowLeft className="od-icon" />
              Back to home
            </Link>
            <p className="page-eyebrow">Welcome back</p>
            <h1>Sign in to your workspace</h1>
            <p className="auth-description">{t('auth.enterCredentials')}</p>

            <Button
              type="button"
              variant="outline"
              className="w-full mb-6"
              onClick={handleSsoLogin}
              disabled={isLoading}
            >
              <KeyRound className="od-icon mr-2" />
              Continue with AuthorizeMe
            </Button>

            <div className="auth-divider">
              <span />
              {t('auth.orUseEmail', { defaultValue: 'Or use your email' })}
              <span />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="od-field [--od-gap:8px]">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="od-field [--od-gap:8px]">
                <div className="od-row">
                  <Label htmlFor="password" className="od-fill">{t('auth.password')}</Label>
                  <Link to="/forgot-password" className="text-primary text-xs hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="password-field">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff className="od-icon" /> : <Eye className="od-icon" />}
                  </Button>
                </div>
              </div>

              {error && (
                <div className="request-notice error !m-0" role="alert">
                  <AlertCircle className="od-icon" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="od-icon mr-2 animate-spin" />
                    {t('auth.loggingIn')}
                  </>
                ) : (
                  <>
                    {t('auth.login')}
                    <ArrowRight className="od-icon ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="auth-register">
              New to AirGap Suite?{' '}
              <Link to="/register" className="text-primary hover:underline">Create a workspace</Link>
            </p>
            <p className="auth-help">
              Use the account assigned to your organization. Contact your administrator if you need access.
            </p>
    </AuthLayout>
  );
}

export default Login;
